import { PAYMENT_METHODS } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, errorMessage, secondaryButton, selectClass } from '../../components/common';
import { t, type MessageKey } from '../../i18n';
import { formatMinor } from '../../lib/money';

interface Fine {
  id: string;
  title: string;
  borrowerName: string;
  reason: 'overdue' | 'lost' | 'damaged';
  amountMinor: number;
  status: 'unpaid' | 'paid' | 'waived';
  receiptNumber: string | null;
}

export function FinesTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [method, setMethod] = useState('cash');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const fines = useQuery({
    queryKey: ['library', schoolId, 'fines'],
    queryFn: async () => {
      const res = await apiList<Fine>(`/schools/${schoolId}/library/fines`, {
        query: { pageSize: 50 },
      });
      return { rows: res.data, currency: (res as unknown as { currency: string }).currency };
    },
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['library', schoolId] });
  const collect = useMutation({
    mutationFn: (id: string) =>
      api<{ receiptNumber: string }>(`/schools/${schoolId}/library/fines/${id}/collect`, {
        method: 'POST',
        body: { method },
      }),
    onSuccess: (fine) => {
      setMessage({ kind: 'ok', text: t('lib.collected', { number: fine.receiptNumber }) });
      refresh();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const waive = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/library/fines/${id}/waive`, {
        method: 'POST',
        body: { reason: reasons[id] },
      }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const currency = fines.data?.currency ?? 'USD';

  return (
    <section aria-label={t('lib.tab.fines')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {can('library.fines', schoolId) && (
        <div>
          <label htmlFor="fine-method" className="block text-sm font-medium">
            {t('fees.method')}
          </label>
          <select
            id="fine-method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className={selectClass}
          >
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {t(`fees.method.${m}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
      )}
      {fines.data?.rows.length === 0 && <p className="text-slate-600">{t('lib.noFines')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {fines.data?.rows.map((f) => (
          <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
            <span>
              {t('lib.fineFor', {
                reason: t(`lib.reason.${f.reason}` as MessageKey),
                title: f.title,
              })}{' '}
              · {f.borrowerName} · <strong>{formatMinor(f.amountMinor, currency)}</strong> ·{' '}
              {t(`lib.status.${f.status}` as MessageKey)}
              {f.receiptNumber ? ` (${f.receiptNumber})` : ''}
            </span>
            {f.status === 'unpaid' && (
              <span className="flex flex-wrap items-center gap-2">
                {can('library.fines', schoolId) && (
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('lib.collect')}: ${f.title} ${f.borrowerName}`}
                    disabled={collect.isPending}
                    onClick={() => collect.mutate(f.id)}
                  >
                    {t('lib.collect')}
                  </button>
                )}
                {can('library.waive_fines', schoolId) && (
                  <>
                    <input
                      aria-label={`${t('lib.waiveReason')}: ${f.title} ${f.borrowerName}`}
                      placeholder={t('lib.waiveReason')}
                      value={reasons[f.id] ?? ''}
                      onChange={(e) => setReasons({ ...reasons, [f.id]: e.target.value })}
                      className="w-40 rounded border border-slate-300 px-2 py-1 text-xs"
                    />
                    <button
                      type="button"
                      className={secondaryButton}
                      aria-label={`${t('lib.waive')}: ${f.title} ${f.borrowerName}`}
                      disabled={!reasons[f.id]?.trim() || waive.isPending}
                      onClick={() => waive.mutate(f.id)}
                    >
                      {t('lib.waive')}
                    </button>
                  </>
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
